import type { ArchiveReason } from "./types";

/**
 * Display formatters for the signals surface. Copy follows spec §5: sentence
 * case, active voice, no "stale" in UI text, no em dashes.
 */

const ARCHIVE_REASON_LABELS: Record<ArchiveReason, string> = {
  no_strategic_fit: "No strategic fit",
  bad_timing: "Bad timing",
  not_relevant: "Not relevant",
  already_engaged: "Already engaged",
  other: "Other",
};

/** Archive-menu labels (spec §5). */
export function archiveReasonLabel(reason: ArchiveReason): string {
  return ARCHIVE_REASON_LABELS[reason];
}

export function relativeTime(iso: string | null): string {
  if (!iso) return "recently";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "recently";
  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  return shortDate(iso);
}

/** "Mon D" — used for "First seen" and follow-up dates (spec §5). */
export function shortDate(iso: string | null): string {
  if (!iso) return "unknown date";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "unknown date";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "Mon D, YYYY" — the nav "Last update" stamp (redesign nav logo block). */
export function longDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** A nav freshness stamp value: the long date, or an honest "None on record"
 *  when the timestamp is null or unparseable. */
export function freshnessStamp(iso: string | null): string {
  return longDate(iso) ?? "None on record";
}

/**
 * "First L." — the redesign ownership chips read "Claimed · Robert R." The
 * sealed mock abbreviates every owner to first name + last initial; the `.mine`
 * vs `.other` chip material carries whose it is.
 */
export function abbreviateName(full: string | null): string {
  if (!full) return "a colleague";
  const parts = full.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return full;
  if (parts.length === 1) return first;
  const last = parts[parts.length - 1] ?? "";
  const initial = last.charAt(0).toUpperCase();
  return initial ? `${first} ${initial}.` : first;
}

/** First name only — ownership chips read "Assigned to {first name}" (spec §3). */
export function firstName(full: string | null): string {
  if (!full) return "a colleague";
  const first = full.trim().split(/\s+/)[0];
  return first || full;
}

/** S6 · ConfidenceLabel. Accepts a 0–1 fraction or a 0–100 value. */
export function confidenceLabel(n: number | null): string | null {
  if (n == null) return null;
  const v = n <= 1 ? n * 100 : n;
  if (v >= 75) return "High confidence";
  if (v >= 50) return "Medium confidence";
  return "Low confidence";
}
